import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { trpc } from '@/lib/trpc';

interface ItineraryFormData {
  dayNumber: number;
  title: string;
  description?: string;
  activities?: string;
  meals: string[];
  accommodation?: string;
}

interface ItineraryModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  packageId: string;
  itinerary?: {
    id: string;
    dayNumber: number;
    title: string;
    description: string | null;
    activities: string[];
    meals: string[];
    accommodation: string | null;
  };
  onSuccess?: () => void;
}

export function ItineraryModal({ open, onOpenChange, packageId, itinerary, onSuccess }: ItineraryModalProps) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const isEditing = !!itinerary;

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    setValue,
    watch,
  } = useForm<ItineraryFormData>({
    defaultValues: itinerary
      ? {
          dayNumber: itinerary.dayNumber,
          title: itinerary.title,
          description: itinerary.description ?? undefined,
          activities: itinerary.activities.join(', '),
          meals: itinerary.meals,
          accommodation: itinerary.accommodation ?? undefined,
        }
      : {
          dayNumber: 1,
          meals: [],
        },
  });

  const utils = trpc.useUtils();

  const createMutation = trpc.package.addItineraryDay.useMutation({
    onSuccess: () => {
      utils.package.byId.invalidate(packageId);
      reset();
      onOpenChange(false);
      onSuccess?.();
    },
    onError: () => {
      setIsSubmitting(false);
    },
  });

  const updateMutation = trpc.package.updateItineraryDay.useMutation({
    onSuccess: () => {
      utils.package.byId.invalidate(packageId);
      onOpenChange(false);
      onSuccess?.();
    },
    onError: () => {
      setIsSubmitting(false);
    },
  });

  const onSubmit = (data: ItineraryFormData) => {
    setIsSubmitting(true);

    const activities = data.activities
      ? data.activities.split(',').map((a) => a.trim()).filter(Boolean)
      : [];

    const mealsTyped = data.meals as ('BREAKFAST' | 'LUNCH' | 'DINNER')[];

    if (isEditing && itinerary) {
      updateMutation.mutate({
        id: itinerary.id,
        data: {
          dayNumber: data.dayNumber,
          title: data.title,
          description: data.description || null,
          activities,
          meals: mealsTyped,
          accommodation: data.accommodation || null,
        },
      });
    } else {
      createMutation.mutate({
        packageId,
        dayNumber: data.dayNumber,
        title: data.title,
        description: data.description,
        activities,
        meals: mealsTyped,
        accommodation: data.accommodation,
      });
    }
  };

  const meals = watch('meals') || [];

  const toggleMeal = (meal: 'BREAKFAST' | 'LUNCH' | 'DINNER') => {
    const currentMeals = meals;
    if (currentMeals.includes(meal)) {
      setValue('meals', currentMeals.filter((m) => m !== meal));
    } else {
      setValue('meals', [...currentMeals, meal]);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>{isEditing ? 'Edit Day' : 'Add Day'}</DialogTitle>
          <DialogDescription>
            {isEditing
              ? 'Update the itinerary day information below.'
              : 'Add a new day to the itinerary.'}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="dayNumber">Day Number *</Label>
              <Input
                id="dayNumber"
                type="number"
                min="1"
                {...register('dayNumber', {
                  required: 'Day number is required',
                  valueAsNumber: true,
                  min: { value: 1, message: 'Day number must be at least 1' },
                })}
                placeholder="1"
              />
              {errors.dayNumber && (
                <p className="text-sm text-red-500">{errors.dayNumber.message}</p>
              )}
            </div>
            <div className="space-y-2">
              <Label htmlFor="title">Title *</Label>
              <Input
                id="title"
                {...register('title', { required: 'Title is required' })}
                placeholder="Arrival & City Tour"
              />
              {errors.title && (
                <p className="text-sm text-red-500">{errors.title.message}</p>
              )}
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="description">Description</Label>
            <Textarea
              id="description"
              {...register('description')}
              placeholder="Describe what happens on this day..."
              rows={3}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="activities">Activities</Label>
            <Input
              id="activities"
              {...register('activities')}
              placeholder="Hot air balloon ride, Cave church visit (comma-separated)"
            />
            <p className="text-xs text-muted-foreground">Separate multiple activities with commas</p>
          </div>

          <div className="space-y-2">
            <Label>Meals Included</Label>
            <div className="flex gap-4">
              {(['BREAKFAST', 'LUNCH', 'DINNER'] as const).map((meal) => (
                <label key={meal} className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={meals.includes(meal)}
                    onChange={() => toggleMeal(meal)}
                    className="h-4 w-4 rounded border-gray-300"
                  />
                  <span className="text-sm capitalize">{meal.toLowerCase()}</span>
                </label>
              ))}
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="accommodation">Accommodation</Label>
            <Input
              id="accommodation"
              {...register('accommodation')}
              placeholder="Cave Hotel in Goreme"
            />
          </div>

          {/* Error Messages */}
          {(createMutation.error || updateMutation.error) && (
            <div className="rounded-md bg-red-50 p-3 text-sm text-red-700">
              {createMutation.error?.message || updateMutation.error?.message}
            </div>
          )}

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {isEditing ? 'Save Changes' : 'Add Day'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
